import { CheckCircle, Circle, ArrowRight, Info, Zap } from 'lucide-react';

export default function ApprovalWorkflowVisualization({ document, currentStatus }) {
  const statuses = ['draft', 'internal_review', 'client_review', 'approved'];
  const statusLabels = {
    draft: 'Draft',
    internal_review: 'Internal Review',
    client_review: 'Client Review',
    approved: 'Approved',
    denied: 'Denied',
    revision: 'Revision',
  };

  const statusDescriptions = {
    draft: 'Initial creation',
    internal_review: 'Team review in progress',
    client_review: 'Awaiting your approval',
    approved: 'Fully approved',
    denied: 'Rejected',
    revision: 'Changes requested',
  };

  const getCurrentIndex = () => {
    return statuses.indexOf(currentStatus);
  };

  const currentIndex = getCurrentIndex();

  return (
    <div className="space-y-6">
      {/* Main Flow */}
      <div>
        <div className="flex items-center gap-2 mb-6">
          <h3 className="font-semibold text-secondary-900">Approval Flow</h3>
          <Zap size={16} className="text-blue-600" />
          <div className="flex-1 h-px bg-secondary-200"></div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-transparent rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 overflow-x-auto px-2 pt-3 pb-2">
            {statuses.map((status, index) => {
              const isActive = currentIndex >= index;
              const isCurrent = currentIndex === index;
              
              return (
                <div key={status} className="flex items-center gap-3 flex-shrink-0">
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 transform ${
                      isCurrent ? 'scale-125 ring-4 ring-offset-2 ring-blue-300 shadow-lg bg-blue-600 text-white' :
                      isActive ? 'bg-green-600 text-white shadow-md' :
                      'bg-secondary-200 text-secondary-500'
                    }`}>
                      {isActive ? (
                        <CheckCircle size={22} />
                      ) : (
                        <Circle size={22} />
                      )}
                    </div>
                    <span className={`text-xs font-semibold mt-2 text-center w-20 leading-tight transition-colors ${
                      isCurrent ? 'text-blue-600' :
                      isActive ? 'text-green-600' :
                      'text-secondary-500'
                    }`}>
                      {statusLabels[status]}
                    </span>
                    <span className="text-xs text-secondary-500 mt-1 text-center w-24">
                      {statusDescriptions[status]}
                    </span>
                  </div>
                  
                  {index < statuses.length - 1 && (
                    <ArrowRight size={20} className={`transition-colors duration-300 ${
                      isActive ? 'text-green-600 animate-pulse' : 'text-secondary-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Progress indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-secondary-600">
            <Zap size={16} className="text-blue-600" />
            <span>Progress: <span className="font-semibold">{Math.round(((currentIndex + 1) / statuses.length) * 100)}%</span></span>
          </div>
          <div className="w-32 bg-secondary-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-500 rounded-full"
              style={{ width: `${((currentIndex + 1) / statuses.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Alternative Paths */}
      <div className="border-t border-secondary-200 pt-6">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-semibold text-secondary-900">Alternative Paths</h3>
          <div className="flex-1 h-px bg-secondary-200"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Revision Path */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center">
                <ArrowRight size={14} className="text-white" />
              </div>
              <h4 className="font-medium text-orange-900">Revision Requested</h4>
            </div>
            <p className="text-sm text-orange-800">
              Document returned to team for updates. Will restart review cycle after changes.
            </p>
          </div>

          {/* Denied Path */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">×</span>
              </div>
              <h4 className="font-medium text-red-900">Denied</h4>
            </div>
            <p className="text-sm text-red-800">
              Document rejected and cannot proceed. Team will be notified to resubmit.
            </p>
          </div>
        </div>
      </div>

      {/* Approval History */}
      {document.approvals && document.approvals.length > 0 && (
        <div className="border-t border-secondary-200 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Info size={18} className="text-blue-600" />
            <h3 className="font-semibold text-secondary-900">Approval Information</h3>
          </div>

          <div className="space-y-3">
            {document.approvals.map((approval, index) => (
              <div key={index} className={`border-l-4 pl-4 py-2 ${
                approval.status === 'approved' ? 'border-green-600 bg-green-50' :
                approval.status === 'revision' ? 'border-orange-600 bg-orange-50' :
                'border-red-600 bg-red-50'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-secondary-900">
                    {approval.approvedBy?.firstName} {approval.approvedBy?.lastName}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${
                    approval.status === 'approved' ? 'bg-green-200 text-green-800' :
                    approval.status === 'revision' ? 'bg-orange-200 text-orange-800' :
                    'bg-red-200 text-red-800'
                  }`}>
                    {statusLabels[approval.status] || approval.status}
                  </span>
                </div>
                <p className="text-sm text-secondary-500 mt-1">
                  {new Date(approval.timestamp).toLocaleString()}
                </p>
                {approval.notes && (
                  <p className="text-sm text-secondary-600 mt-2 italic">{approval.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
